import { apiClient } from "./client";

/**
 * Odontogram + dental treatment-plan API (ticket #18617/#18636). DENTAL vertical only — the backend
 * gates every path and `capability()` lets the UI hide the feature for non-dental clinics. Endpoints
 * mirror `OdontogramController` (`/api/v1/dental/*`).
 */
const BASE = "/v1/dental";

export interface DentalCapability {
  enabled: boolean;
  vertical: string;
}

export interface ToothState {
  toothFdi: string;
  condition: string;
  surfaces: string | null;
  note: string | null;
}

export interface DentalFinding {
  id: string;
  toothFdi: string;
  surface: string | null;
  condition: string;
  note: string | null;
  encounterId: string | null;
  recordedAt: string;
}

export interface Odontogram {
  vertical: string;
  teeth: ToothState[];
  recentFindings: DentalFinding[];
}

export interface DentalPlanItem {
  id: string;
  toothFdi: string | null;
  serviceId: string | null;
  patientPackageId: string | null;
  description: string;
  sessionNo: number | null;
  status: string;
  doneEncounterId: string | null;
  doneAt: string | null;
}

export interface DentalPlan {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  items: DentalPlanItem[];
}

export interface RecordFindingRequest {
  toothFdi: string;
  surface?: string | null;
  condition: string;
  note?: string | null;
  encounterId?: string | null;
}

export interface AddPlanItemRequest {
  toothFdi?: string | null;
  serviceId?: string | null;
  patientPackageId?: string | null;
  description: string;
  sessionNo?: number | null;
}

export const dentalApi = {
  capability: () => apiClient.get<DentalCapability>(`${BASE}/capability`).then((r) => r.data),
  getChart: (patientId: string) =>
    apiClient.get<Odontogram>(`${BASE}/patients/${patientId}/odontogram`).then((r) => r.data),
  recordFinding: (patientId: string, req: RecordFindingRequest) =>
    apiClient.post<DentalFinding>(`${BASE}/patients/${patientId}/findings`, req).then((r) => r.data),
  listPlans: (patientId: string) =>
    apiClient.get<DentalPlan[]>(`${BASE}/patients/${patientId}/treatment-plans`).then((r) => r.data),
  createPlan: (patientId: string, title: string) =>
    apiClient
      .post<string>(`${BASE}/patients/${patientId}/treatment-plans`, { title })
      .then((r) => r.data),
  addPlanItem: (planId: string, req: AddPlanItemRequest) =>
    apiClient.post<string>(`${BASE}/treatment-plans/${planId}/items`, req).then((r) => r.data),
  completeItem: (itemId: string, encounterId?: string | null) =>
    apiClient
      .post<void>(`${BASE}/treatment-plan-items/${itemId}/complete`, {
        encounterId: encounterId ?? null,
      })
      .then((r) => r.data),
};

export const dentalQueryKeys = {
  capability: () => ["dental", "capability"] as const,
  chart: (patientId: string) => ["dental", "chart", patientId] as const,
  plans: (patientId: string) => ["dental", "plans", patientId] as const,
};
